import Route from '@ember/routing/route'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { restartableTask, timeout } from 'ember-concurrency'
import { Product } from 'teacher-ui-client/utils/products'
import { toRequestScopeStudent } from 'teacher-ui-client/services/reporting'
import type { RequestScope, ReportingQueryPage, ReportingQuerySort } from 'teacher-ui-client/services/reporting'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type ReadingEggspressDashboardStudentRoute from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/route'
import type {
  BooksReadOverviewReportData,
  BooksReadEventsReportData,
} from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/index/route'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import type FileDownload from 'teacher-ui-client/services/file-download'
import { formatExcel } from 'teacher-ui-client/utils/dates'
import { reportingFilename } from 'teacher-ui-client/utils/filename'
import type { Params as BooksReadIndexParams } from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/books-read/index/route'
import type { NormalizedInterval } from 'date-fns'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'

export default class extends Route {
  @service reporting!: ReportingService

  @service intl!: IntlService

  @service fileDownload!: FileDownload

  @service flashQueue!: FlashQueueService

  buildRouteInfoMetadata() {
    return {
      pageTitle: this.intl.t('pages.reporting.readingEggspress.dashboard.student.booksRead.title'),
      exportOptions: [
        {
          exportType: ExportType.CSV,
          exportAction: () => this.exportCsv(),
        },
        { exportType: ExportType.print },
      ],
    }
  }

  model() {
    const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

    const { student } = this.modelFor(
      'reporting.ui-scope.rex.dashboard.student',
    ) as ModelFor<ReadingEggspressDashboardStudentRoute>
    const requestScope = toRequestScopeStudent(student.id)

    void this.fetchBooksReadOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})

    // performing the events task is left up to the sub-route, where it has page/sort query params.
    // this is done in a sub-route to ensure the tasks in this model are not recalled when those QPs change.

    return {
      period,
      requestScope,
      fetchBooksReadOverviewReportTask: this.fetchBooksReadOverviewReportTask,
      fetchBooksReadEventsReportTask: this.fetchBooksReadEventsReportTask,
    }
  }

  fetchBooksReadOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchBooksReadOverviewReport>) => {
      await timeout(200)
      return this.fetchBooksReadOverviewReport(...args)
    },
  )

  fetchBooksReadEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchBooksReadEventsReport>) => {
      await timeout(200)
      return this.fetchBooksReadEventsReport(...args)
    },
  )

  fetchBooksReadOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/books-read/overview'
    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })
    const query = this.reporting.buildQuery({ period })
    return this.reporting.fetchReport<BooksReadOverviewReportData>(url, query)
  }

  fetchBooksReadEventsReport(
    product: Product,
    sort: ReportingQuerySort,
    page: ReportingQueryPage,
    period: NormalizedInterval,
    requestScope: RequestScope,
  ) {
    const reportName = 'student-dashboard/books-read/events'
    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })
    const query = this.reporting.buildQuery({ sort, page, period })
    return this.reporting.fetchReport<BooksReadEventsReportData>(url, query)
  }

  async exportCsv() {
    const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>
    const { student } = this.modelFor(
      'reporting.ui-scope.rex.dashboard.student',
    ) as ModelFor<ReadingEggspressDashboardStudentRoute>
    const requestScope = toRequestScopeStudent(student.id)
    const { sort } = this.paramsFor('reporting.ui-scope.rex.dashboard.student.books-read.index') as BooksReadIndexParams
    const page = 'all'

    let overviewReport: Awaited<ReturnType<typeof this.fetchBooksReadOverviewReport>>
    let eventsReport: Awaited<ReturnType<typeof this.fetchBooksReadEventsReport>>
    try {
      overviewReport = await this.fetchBooksReadOverviewReport(Product.REX, period, requestScope)
      eventsReport = await this.fetchBooksReadEventsReport(Product.REX, sort, page, period, requestScope)
    } catch {
      this.flashQueue.addFail({ subtitle: this.intl.t('failedToExportReport') })
      return
    }

    const overviewHeader = [
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.classification'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.daysRead'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.booksRead'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.chaptersRead'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.wordsRead'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.questions'),
      `${this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.averageScore')} %`,
    ]
    const overviewRows = [
      [
        'fiction',
        overviewReport.overview.classifications.fiction.days_read,
        overviewReport.overview.classifications.fiction.books_read,
        overviewReport.overview.classifications.fiction.chapters_read,
        overviewReport.overview.classifications.fiction.words_read,
        overviewReport.overview.classifications.fiction.questions,
        overviewReport.overview.classifications.fiction.average_score,
      ],
      [
        'nonfiction',
        overviewReport.overview.classifications.nonfiction.days_read,
        overviewReport.overview.classifications.nonfiction.books_read,
        overviewReport.overview.classifications.nonfiction.chapters_read,
        overviewReport.overview.classifications.nonfiction.words_read,
        overviewReport.overview.classifications.nonfiction.questions,
        overviewReport.overview.classifications.nonfiction.average_score,
      ],
      [
        'total',
        '-',
        overviewReport.overview.total.books_read,
        overviewReport.overview.total.chapters_read,
        overviewReport.overview.total.words_read,
        overviewReport.overview.total.questions,
        '-',
      ],
    ]

    const eventHeader = [
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.date'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.bookChapter'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.classification'),
      `${this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.lexile')} L`,
    ]
    const eventRows = eventsReport.events.map((ev) => {
      let bookName = ev.book_name
      if (ev.chapter) {
        bookName += ` - ${this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.chapter', {
          chapter: ev.chapter,
        })}`
      }
      return [formatExcel(new Date(ev.created_at)), bookName, ev.classification, ev.lexile_formatted]
    })

    const overviewFilename = `${reportingFilename('books-read-overview', student.fullName, period)}.csv`
    const eventsFilename = `${reportingFilename('books-read-recent-activity', student.fullName, period)}.csv`

    this.fileDownload.downloadAsCsv([overviewHeader, ...overviewRows], { filename: overviewFilename, header: true })
    this.fileDownload.downloadAsCsv([eventHeader, ...eventRows], { filename: eventsFilename, header: true })
  }
}
